import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useMatch, useOutletContext } from 'react-router-dom'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Body1, Heading4, Heading6, Title1 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { Link } from '@ui/components/Link'
import { ChartBarIcon } from '@ui/icons/ChartBar.icon'
import { ChartDonutIcon } from '@ui/icons/ChartDonut.icon'
import { ChatCircleDotsIcon } from '@ui/icons/ChatCircleDots.icon'
import { DevicesIcon } from '@ui/icons/Devices.icon'
import { GraphIcon } from '@ui/icons/Graph.icon'
import { LayersIcon } from '@ui/icons/Layers.icon'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'

import { PlatformInfoDto } from '@model/PlatformInfoDto'
import { formatPlatformName, getPlatformTypes } from '@model/PlatformTypeEnum'

import { Locale } from '@global/locale/Locale'

import { useFetchOne } from '@hooks/useFetch'
import { useHomePageLink } from '@hooks/useHomePageUrl'
import { ScrollToIds, useScrollToElementIds } from '@hooks/useScrollToElementIds'
import { useScrollToTop } from '@hooks/useScrollToTop'

import { Box } from '@shared/components/Box'

import tradingviewRightDesktop from '../images/TradingView/tradingview-right-desktop.webp'
import tradingviewRightMobile from '../images/TradingView/tradingview-right-mobile.webp'
import { PlatformDownloadButton } from './PlatformIcon'
import { TradingViewBrokerReviewWidget } from './TradingViewBrokerReview'
import { usePlatformTradingViewDownloadLinksData } from './platformsTradingViewData'

import styles from './PlatformsTradingViewPage.module.scss'

interface TradingViewProps {
  platformInfo: PlatformInfoDto[]
}

export const PlatformsTradingViewPage: React.FC = () => {
  useScrollToTop()

  const { platformInfo = [] } = useOutletContext<TradingViewProps>()

  const isMobile = useWindowResize()
  const { scrollIntoView } = useScrollToElementIds()
  const locale = useSessionLanguage()

  const match = useMatch(`/${locale}/dashboard/tools/platforms/:platformType`)
  const platformType = match?.params?.platformType || ''

  useEffect(() => {
    const platformNames = getPlatformTypes().map((x) => x.shortName)
    if (platformNames.some((name) => platformType.includes(formatPlatformName(name)))) {
      scrollIntoView([ScrollToIds.ToolsPlatformsHeader])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformType])

  return isMobile ? (
    <TradingViewMobile platformInfo={platformInfo} />
  ) : (
    <TradingViewDesktop platformInfo={platformInfo} />
  )
}

const TradingViewMobile: React.FC<TradingViewProps> = (props) => {
  const { platformInfo } = props

  const { t } = useTranslation()
  const homePageUrl = useHomePageLink()

  const { getLocalizedVideoUrl } = useLocalizedVideos()

  return (
    <>
      <div className={styles.downloadSection}>
        <img src={tradingviewRightMobile} className={styles.hasMarginBottom} alt='' />
        <Heading4 isParagraph className={styles.hasMarginBottom} medium>
          {t('ToolsPlatforms.Unleash your trading skills with TradingView')}
        </Heading4>
        <Body1 className={styles.textSecondary} isParagraph>
          {t('ToolsPlatforms.TradingViewInfo.1')} {t('ToolsPlatforms.TradingViewInfo.2')}{' '}
          {t('ToolsPlatforms.TradingViewInfo.3')}
        </Body1>
        <div className={styles.linksSectionAbout}>
          <div className={styles.linksSection}>
            <DownloadLinks platformInfo={platformInfo} />
          </div>
        </div>
      </div>
      <div className='p-4'>
        <div className={styles.analysisHeader}>
          <ChartDonutIcon color='text-primary-med-em' />
          <Title1>{t('ToolsPlatforms.Advanced visual analysis')}</Title1>
        </div>
        <Body1 className={styles.textSecondary}>
          {t(
            'ToolsPlatforms.Enhance an informed trading approach with over 110+ intelligent drawing tools'
          )}
        </Body1>
      </div>
      <div className='p-4'>
        <div className={styles.analysisHeader}>
          <LayersIcon color='text-primary-med-em' />
          <Title1>{t('ToolsPlatforms.Widest choice of indicators')}</Title1>
        </div>
        <Body1 className={styles.textSecondary}>
          {t('ToolsPlatforms.The technical analysis brings limitless opportunities')}
        </Body1>
      </div>
      <div className='p-4'>
        <div className={styles.analysisHeader}>
          <ChartBarIcon color='text-primary-med-em' />
          <Title1>{t('ToolsPlatforms.Unparalleled charting interface')}</Title1>
        </div>
        <Body1 className={styles.textSecondary}>
          {t('ToolsPlatforms.Regardless of where you are in your trading path')}
        </Body1>
      </div>
      <Heading4 isParagraph className={styles.hasMarginTop} medium>
        {t('ToolsPlatforms.Connect TradingView with your Tickmill Account')}
      </Heading4>
      <div className={styles.stepsSection}>
        <div className={styles.step}>
          <NumberBox number={1} />
          <div>
            <Title1 isParagraph medium>
              {t('ToolsPlatforms.Set up accounts')}
            </Title1>
            <Body1 className={styles.textSecondary}>
              {t(
                'ToolsPlatforms.Create or use an existing Tickmill TradingView account and make sure you have a TradingView profile'
              )}
            </Body1>
          </div>
        </div>
        <div className={styles.step}>
          <NumberBox number={2} />
          <div>
            <Title1 isParagraph medium>
              {t('ToolsPlatforms.Launch TradingView from Tickmill')}
            </Title1>
            <Body1 className={styles.textSecondary}>
              {t('ToolsPlatforms.Head to the')}{' '}
              <Link to='/dashboard/traders-room/trading-accounts' size='lg'>
                {t('Trading Account.Trading Accounts')}
              </Link>{' '}
              {t('ToolsPlatforms.section and click Trade')}
            </Body1>
          </div>
        </div>
        <div className={styles.step}>
          <NumberBox number={3} />
          <div>
            <Title1 isParagraph medium>
              {t('ToolsPlatforms.Log in with your Tickmill credentials')}
            </Title1>
            <Body1 className={styles.textSecondary}>
              {t('ToolsPlatforms.Enter your TradingView account credentials when prompted.')}
            </Body1>
          </div>
        </div>
        <div className={styles.step}>
          <NumberBox number={4} />
          <div>
            <Title1 isParagraph medium>
              {t('ToolsPlatforms.You are ready to trade')}
            </Title1>
            <Body1 className={styles.textSecondary}>
              {t(
                'ToolsPlatforms.You are now connected and ready to trade across all TradingView platforms'
              )}
            </Body1>
          </div>
        </div>
      </div>
      <div className={styles.tradingviewVideo}>
        <video src={getLocalizedVideoUrl('tmt-21095-trading-view')} controls />
      </div>
      <Heading4 medium>{t('ToolsPlatforms.TradingView platform features')}</Heading4>
      <div>
        <div className={styles.featureHeader}>
          <DevicesIcon size={40} color='text-primary-med-em' />
          <Heading6 medium>{t('ToolsPlatforms.Multi-platform experience')}</Heading6>
        </div>
        <Body1 className={styles.textSecondary}>
          {t('ToolsPlatforms.Take advantage of TradingViews powerful browser')}
        </Body1>
      </div>
      <div>
        <div className={styles.featureHeader}>
          <GraphIcon size={40} color='text-primary-med-em' />
          <Heading6 medium>{t('ToolsPlatforms.Charts that move markets')}</Heading6>
        </div>
        <Body1 className={styles.textSecondary}>
          {t('ToolsPlatforms.Whether youd like to simply look up the latest stock price')}
        </Body1>
      </div>
      <div>
        <div className={styles.featureHeader}>
          <ChatCircleDotsIcon size={40} color='text-primary-med-em' />
          <Heading6 medium>{t('ToolsPlatforms.TradingView community')}</Heading6>
        </div>
        <Body1 className={styles.textSecondary}>
          {t('ToolsPlatforms.Get inspiration for your next trade. Dont reinvent the wheel')}
        </Body1>
      </div>
      <div className={styles.buttonSection}>
        <div className={styles.button}>
          <Link to={`${homePageUrl}/trading-platforms/tradingview`} fullWidth>
            <Button.Primary size='md' fullWidth>
              {t('Learn More')}
            </Button.Primary>
          </Link>
        </div>
      </div>
      <div className={styles.videoGuidesSection}>
        <div className={styles.guideTitle}>
          <Heading4 medium>{t('ToolsPlatforms.New to TradingView?')}</Heading4>
          <Body1 className={styles.textSecondary}>
            {t('ToolsPlatforms.Below you will find video guides to get you started.')}
          </Body1>
        </div>
        <div className={styles.videoGuidesList}>
          <div className={styles.videoGuideItem}>
            <Heading6 medium>{t('ToolsPlatforms.How to Set Up a Chart on TradingView')}</Heading6>
            <video
              src='https://d1fyjtrsl71uh.cloudfront.net/videos/video-tutorials/trading-view-videos/how-to-set-up-a-chart-on-tradingview.mp4'
              controls
            />
          </div>

          <div className={styles.videoGuideItem}>
            <Heading6 medium>{t('ToolsPlatforms.How to Use Alerts on TradingView')}</Heading6>
            <video
              src='https://d1fyjtrsl71uh.cloudfront.net/videos/video-tutorials/trading-view-videos/how-to-use-alerts-on-tradingview.mp4'
              controls
            />
          </div>

          <div className={styles.videoGuideItem}>
            <Heading6 medium>
              {t('ToolsPlatforms.How to Use Drawing Tools on TradingView')}
            </Heading6>
            <video
              src='https://d1fyjtrsl71uh.cloudfront.net/videos/video-tutorials/trading-view-videos/how-to-use-drawing-tools-on-tradingview.mp4'
              controls
            />
          </div>

          <div className={styles.videoGuideItem}>
            <Heading6 medium>{t('ToolsPlatforms.How to Use Indicators on TradingView')}</Heading6>
            <video
              src='https://d1fyjtrsl71uh.cloudfront.net/videos/video-tutorials/trading-view-videos/how-to-use-indicators-on-tradingview.mov'
              controls
            />
          </div>
        </div>
      </div>
      <TradingViewBrokerReviewWidget />
    </>
  )
}

const TradingViewDesktop: React.FC<TradingViewProps> = (props) => {
  const { platformInfo } = props

  const { t } = useTranslation()

  const homePageUrl = useHomePageLink()
  const { getLocalizedVideoUrl } = useLocalizedVideos()

  return (
    <div>
      <div className={styles.container}>
        <div className={styles.downloadSection}>
          <div className={styles.textSection}>
            <Heading4 isParagraph medium>
              {t('ToolsPlatforms.Unleash your trading skills with TradingView')}
            </Heading4>
            <Body1 className={styles.textSecondary}>{t('ToolsPlatforms.TradingViewInfo.1')}</Body1>
            <br />
            <Body1 className={styles.textSecondary}>{t('ToolsPlatforms.TradingViewInfo.2')}</Body1>
            <br />
            <Body1 className={styles.textSecondary}>{t('ToolsPlatforms.TradingViewInfo.3')}</Body1>
            <div className={styles.linksSection}>
              <DownloadLinks platformInfo={platformInfo} />
            </div>
          </div>
          <div>
            <img src={tradingviewRightDesktop} alt='' />
          </div>
        </div>
      </div>
      <div className={styles.analysisSection}>
        <div className={styles.item}>
          <div className={styles.analysisHeader}>
            <ChartDonutIcon color='text-primary-med-em' />
            <Title1>{t('ToolsPlatforms.Advanced visual analysis')}</Title1>
          </div>
          <Body1 className={styles.textSecondary}>
            {t(
              'ToolsPlatforms.Enhance an informed trading approach with over 110+ intelligent drawing tools'
            )}
          </Body1>
        </div>
        <div className={styles.item}>
          <div className={styles.analysisHeader}>
            <LayersIcon color='text-primary-med-em' />
            <Title1>{t('ToolsPlatforms.Widest choice of indicators')}</Title1>
          </div>
          <Body1 className={styles.textSecondary}>
            {t('ToolsPlatforms.The technical analysis brings limitless opportunities')}
          </Body1>
        </div>
        <div className={styles.item}>
          <div className={styles.analysisHeader}>
            <ChartBarIcon color='text-primary-med-em' />
            <Title1>{t('ToolsPlatforms.Unparalleled charting interface')}</Title1>
          </div>
          <Body1 className={styles.textSecondary}>
            {t('ToolsPlatforms.Regardless of where you are in your trading path')}
          </Body1>
        </div>
      </div>
      <div className={styles.connectSection}>
        <div className={styles.chartContainer}>
          <video src={getLocalizedVideoUrl('tmt-21095-trading-view')} controls />
        </div>
        <div className={styles.stepsContainer}>
          <div className={styles.stepsHeader}>
            <Heading4 medium>
              {t('ToolsPlatforms.Connect TradingView with your Tickmill Account')}
            </Heading4>
          </div>
          <div className={styles.step}>
            <NumberBox number={1} />
            <div>
              <Title1 isParagraph medium>
                {t('ToolsPlatforms.Set up accounts')}
              </Title1>
              <Body1 className={styles.textSecondary}>
                {t(
                  'ToolsPlatforms.Create or use an existing Tickmill TradingView account and make sure you have a TradingView profile'
                )}
              </Body1>
            </div>
          </div>
          <div className={styles.step}>
            <NumberBox number={2} />
            <div>
              <Title1 isParagraph medium>
                {t('ToolsPlatforms.Launch TradingView from Tickmill')}
              </Title1>
              <Body1 className={styles.textSecondary}>
                {t('ToolsPlatforms.Head to the')}{' '}
                <Link to='/dashboard/traders-room/trading-accounts' size='lg'>
                  {t('Trading Account.Trading Accounts')}
                </Link>{' '}
                {t('ToolsPlatforms.section and click Trade')}
              </Body1>
            </div>
          </div>
          <div className={styles.step}>
            <NumberBox number={3} />
            <div>
              <Title1 isParagraph medium>
                {t('ToolsPlatforms.Log in with your Tickmill credentials')}
              </Title1>
              <Body1 className={styles.textSecondary}>
                {t('ToolsPlatforms.Enter your TradingView account credentials when prompted.')}
              </Body1>
            </div>
          </div>
          <div className={styles.step}>
            <NumberBox number={4} />
            <div>
              <Title1 isParagraph medium>
                {t('ToolsPlatforms.You are ready to trade')}
              </Title1>
              <Body1 className={styles.textSecondary}>
                {t(
                  'ToolsPlatforms.You are now connected and ready to trade across all TradingView platforms'
                )}
              </Body1>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.title}>
        <Heading4 medium>{t('ToolsPlatforms.TradingView platform features')}</Heading4>
      </div>
      <div className={styles.featuresSection}>
        <div className={styles.item}>
          <div className={styles.featureHeader}>
            <DevicesIcon size={40} color='text-primary-med-em' />
            <Heading6 medium>{t('ToolsPlatforms.Multi-platform experience')}</Heading6>
          </div>
          <Body1 className={styles.textSecondary}>
            {t('ToolsPlatforms.Take advantage of TradingViews powerful browser')}
          </Body1>
        </div>
        <div className={styles.item}>
          <div className={styles.featureHeader}>
            <GraphIcon size={40} color='text-primary-med-em' />
            <Heading6 medium>{t('ToolsPlatforms.Charts that move markets')}</Heading6>
          </div>
          <Body1 className={styles.textSecondary}>
            {t('ToolsPlatforms.Whether youd like to simply look up the latest stock price')}
          </Body1>
        </div>
        <div className={styles.item}>
          <div className={styles.featureHeader}>
            <ChatCircleDotsIcon size={40} color='text-primary-med-em' />
            <Heading6 medium>{t('ToolsPlatforms.TradingView community')}</Heading6>
          </div>
          <Body1 className={styles.textSecondary}>
            {t('ToolsPlatforms.Get inspiration for your next trade. Dont reinvent the wheel')}
          </Body1>
        </div>
      </div>
      <div className={styles.buttonSection}>
        <div className={styles.button}>
          <Link to={`${homePageUrl}/trading-platforms/tradingview`} target='_blank' fullWidth>
            <Button.Primary size='lg' fullWidth>
              {t('Learn More')}
            </Button.Primary>
          </Link>
        </div>
      </div>
      <div className={styles.videoGuidesSection}>
        <div className={styles.guideTitle}>
          <Heading4 medium>{t('ToolsPlatforms.New to TradingView?')}</Heading4>
          <Body1 className={styles.textSecondary}>
            {t('ToolsPlatforms.Below you will find video guides to get you started.')}
          </Body1>
        </div>
        <div className={styles.videoGuidesList}>
          <div className={styles.videoGuideItem}>
            <video
              src='https://d1fyjtrsl71uh.cloudfront.net/videos/video-tutorials/trading-view-videos/how-to-set-up-a-chart-on-tradingview.mp4'
              controls
            />
            <div className={styles.content}>
              <Heading6 medium>{t('ToolsPlatforms.How to Set Up a Chart on TradingView')}</Heading6>
              <Body1 className={styles.textSecondary}>
                {t(
                  'ToolsPlatforms.Quickly learn how to adjust chart types, timeframes, and themes to match your trading style.'
                )}
              </Body1>
            </div>
          </div>

          <div className={styles.videoGuideItem}>
            <video
              src='https://d1fyjtrsl71uh.cloudfront.net/videos/video-tutorials/trading-view-videos/how-to-use-alerts-on-tradingview.mp4'
              controls
            />
            <div className={styles.content}>
              <Heading6 medium>{t('ToolsPlatforms.How to Use Alerts on TradingView')}</Heading6>
              <Body1 className={styles.textSecondary}>
                {t(
                  'ToolsPlatforms.Set price and indicator alerts so you never miss a key market move.'
                )}
              </Body1>
            </div>
          </div>

          <div className={styles.videoGuideItem}>
            <video
              src='https://d1fyjtrsl71uh.cloudfront.net/videos/video-tutorials/trading-view-videos/how-to-use-drawing-tools-on-tradingview.mp4'
              controls
            />
            <div className={styles.content}>
              <Heading6 medium>
                {t('ToolsPlatforms.How to Use Drawing Tools on TradingView')}
              </Heading6>
              <Body1 className={styles.textSecondary}>
                {t(
                  'ToolsPlatforms.Master trend lines, shapes, and tools to boost your technical analysis.'
                )}
              </Body1>
            </div>
          </div>

          <div className={styles.videoGuideItem}>
            <video
              src='https://d1fyjtrsl71uh.cloudfront.net/videos/video-tutorials/trading-view-videos/how-to-use-indicators-on-tradingview.mov'
              controls
            />
            <div className={styles.content}>
              <Heading6 medium>{t('ToolsPlatforms.How to Use Indicators on TradingView')}</Heading6>
              <Body1 className={styles.textSecondary}>
                {t('ToolsPlatforms.Learn to add and tweak indicators for sharper trade decisions.')}
              </Body1>
            </div>
          </div>
        </div>
      </div>
      <TradingViewBrokerReviewWidget />
    </div>
  )
}

const DownloadLinks: React.FC<TradingViewProps> = (props) => {
  const { platformDownloadLinksData } = usePlatformTradingViewDownloadLinksData(props.platformInfo)

  return (
    <>
      {platformDownloadLinksData.map((x) => (
        <PlatformDownloadButton className={styles.hasMarginRight} to={x.link} key={x.link}>
          {x.icon}
        </PlatformDownloadButton>
      ))}
    </>
  )
}

export interface VideoData {
  url: string
  id: string
  category: string
  languageId: Locale
}

export const useLocalizedVideos = (): {
  getLocalizedVideoUrl: (fileName: string) => string
  loading: boolean
} => {
  const locale = useSessionLanguage()

  const apiClient = useApiClient(ClientApiClient)

  const localizeVideoCallback = useCallback(() => {
    return apiClient.getLocalizedVideos(locale === 'tr' ? 'en' : locale)
  }, [apiClient, locale])

  const { data: videos = [], isLoading: loading } = useFetchOne(localizeVideoCallback)

  const getLocalizedVideoUrl = (fileName: string): string => {
    if (loading || videos.length === 0) {
      return fileName
    }

    const matchingVideo = videos.find((v) => v.url.includes(fileName))

    return matchingVideo?.url || fileName
  }

  return { getLocalizedVideoUrl, loading }
}

const NumberBox = ({ number }: { number: number }) => {
  return (
    <Box
      style={{ width: 40, height: 40, minHeight: 40, minWidth: 40 }}
      flex
      align='center'
      justify='center'
      bg='surface-primary-med-em'
      radius='x6'
    >
      <Body1 color='text-white'>{number}</Body1>
    </Box>
  )
}
